import React, { useState } from 'react';
import { CircularProgress, Dialog, DialogTitle, DialogContent, TextField, Select, MenuItem, Button, FormControl, InputLabel } from '@mui/material';
import { useSelector } from 'react-redux';
import { useRisks } from './useCPData';
import cpApi from './cpApi';
import './ArtemyStyles.css';

const SEV = ['Critical', 'High', 'Medium', 'Low'];
const TYPES = ['Dependency', 'Intent Drift', 'Capacity', 'Governance', 'Integration'];
const STATUS_COLORS = { Open: 'var(--artemy-yellow)', 'In Review': 'var(--artemy-accent)', Monitored: 'var(--artemy-text-dim)', Resolved: 'var(--artemy-green)' };
const SEV_COLORS = { Critical: 'var(--artemy-red)', High: 'var(--artemy-red)', Medium: 'var(--artemy-yellow)', Low: 'var(--artemy-accent)' };

function Pill({ label, color }) {
  return <span style={{ fontFamily: 'var(--artemy-font-mono)', fontSize: 10, color, background: `${color}1a`, padding: '2px 8px', borderRadius: 6, whiteSpace: 'nowrap', border: `1px solid ${color}33` }}>{label}</span>;
}

export default function RiskRegistry() {
  const { user } = useSelector((s) => s.auth);
  const { risks, loading, refetch } = useRisks();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: '', team: '', severity: 'Medium', type: 'Dependency', owner: '' });
  const [saving, setSaving] = useState(false);

  const orgCode = user?.data?.[0]?.client_code || '';

  const handleCreate = async () => {
    setSaving(true);
    try {
      await cpApi.createRisk({ ...form, org_code: orgCode });
      setOpen(false);
      setForm({ title: '', team: '', severity: 'Medium', type: 'Dependency', owner: '' });
      refetch();
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const handleResolve = async (risk_id) => {
    try {
      await cpApi.resolveRisk({ org_code: orgCode, risk_id });
      refetch();
    } catch (e) {
      console.error(e);
    }
  };

  const critCount = risks.filter((r) => r.severity === 'Critical' || r.severity === 'High').length;
  const medCount = risks.filter((r) => r.severity === 'Medium').length;
  const resolvedCount = risks.filter((r) => r.status === 'Resolved').length;

  return (
    <div className="artemy-fade-up">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 22 }}>
        <div>
          <div className="artemy-page-title">Risk Registry</div>
          <div className="artemy-page-sub">{risks.length} active risks · {critCount} require immediate action</div>
        </div>
        <button className="artemy-btn artemy-btn-primary" onClick={() => setOpen(true)}>
          + Log Risk
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14, marginBottom: 16 }}>
        {[
          { label: 'Total Active', value: risks.length, color: 'var(--artemy-text-primary)' },
          { label: 'Critical / High', value: critCount, color: 'var(--artemy-red)' },
          { label: 'Medium', value: medCount, color: 'var(--artemy-yellow)' },
          { label: 'Resolved (7d)', value: resolvedCount, color: 'var(--artemy-green)' },
        ].map((m) => (
          <div key={m.label} className="artemy-card" style={{ padding: 16 }}>
            <div style={{ fontFamily: 'var(--artemy-font-mono)', fontSize: 11, color: 'var(--artemy-text-secondary)', marginBottom: 7 }}>{m.label}</div>
            <div style={{ fontFamily: 'var(--artemy-font-display)', fontSize: 26, fontWeight: 700, color: m.color }}>{m.value}</div>
          </div>
        ))}
      </div>

      <div className="artemy-card" style={{ overflow: 'hidden' }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--artemy-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontFamily: 'var(--artemy-font-display)', fontSize: 14, fontWeight: 600 }}>⚠ All Active Risks</div>
          <div style={{ fontFamily: 'var(--artemy-font-mono)', fontSize: 11, color: 'var(--artemy-text-dim)' }}>Sorted by severity</div>
        </div>
        {loading ? (
          <div style={{ padding: 32, display: 'flex', justifyContent: 'center' }}><CircularProgress size={28} sx={{ color: 'var(--artemy-accent)' }} /></div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr>
                {['Severity', 'Risk', 'Team', 'Type', 'Detected', 'Owner', 'Status', ''].map((h) => (
                  <th key={h} style={{ textAlign: 'left', padding: '11px 16px', fontFamily: 'var(--artemy-font-mono)', fontSize: 10, letterSpacing: '.8px', color: 'var(--artemy-text-dim)', textTransform: 'uppercase', borderBottom: '1px solid var(--artemy-border)', fontWeight: 400 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {risks.length === 0 ? (
                <tr><td colSpan={8} style={{ padding: 32, textAlign: 'center', color: 'var(--artemy-text-dim)' }}>No active risks</td></tr>
              ) : (
                risks.map((r, i) => (
                  <tr key={r.risk_id || i} style={{ borderBottom: '1px solid rgba(255,255,255,.03)', transition: 'background .15s' }} className="artemy-tr-hover">
                    <td style={{ padding: '12px 16px' }}><Pill label={r.severity} color={SEV_COLORS[r.severity] || 'var(--artemy-text-secondary)'} /></td>
                    <td style={{ padding: '12px 16px', fontWeight: 600, color: 'var(--artemy-text-primary)' }}>{r.title}</td>
                    <td style={{ padding: '12px 16px', color: 'var(--artemy-text-secondary)' }}>{r.team}</td>
                    <td style={{ padding: '12px 16px' }}><Pill label={r.type} color="var(--artemy-accent)" /></td>
                    <td style={{ padding: '12px 16px', fontFamily: 'var(--artemy-font-mono)', fontSize: 11, color: 'var(--artemy-text-dim)' }}>{r.detected_at || 'recently'}</td>
                    <td style={{ padding: '12px 16px', color: 'var(--artemy-text-secondary)' }}>{r.owner || '—'}</td>
                    <td style={{ padding: '12px 16px' }}><Pill label={r.status || 'Open'} color={STATUS_COLORS[r.status] || 'var(--artemy-yellow)'} /></td>
                    <td style={{ padding: '12px 16px' }}>
                      {r.status !== 'Resolved' && (
                        <span onClick={() => handleResolve(r.risk_id)} style={{ fontSize: 11.5, color: 'var(--artemy-green)', cursor: 'pointer', fontWeight: 500 }}>Resolve</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={open} onClose={() => setOpen(false)} PaperProps={{ sx: { background: 'var(--artemy-bg-surface)', border: '1px solid var(--artemy-border)', borderRadius: 2.5, minWidth: 480, backgroundImage: 'none' } }}>
        <DialogTitle sx={{ fontFamily: 'var(--artemy-font-display)', fontWeight: 700, color: 'var(--artemy-text-primary)', borderBottom: '1px solid var(--artemy-border)', fontSize: 18 }}>Log New Risk</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 4 }}>
            <TextField label="Risk Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} fullWidth size="small" />
            <TextField label="Team" value={form.team} onChange={(e) => setForm({ ...form, team: e.target.value })} fullWidth size="small" />
            <TextField label="Owner" value={form.owner} onChange={(e) => setForm({ ...form, owner: e.target.value })} fullWidth size="small" />
            <FormControl fullWidth size="small">
              <InputLabel>Severity</InputLabel>
              <Select value={form.severity} label="Severity" onChange={(e) => setForm({ ...form, severity: e.target.value })}>
                {SEV.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>Type</InputLabel>
              <Select value={form.type} label="Type" onChange={(e) => setForm({ ...form, type: e.target.value })}>
                {TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </Select>
            </FormControl>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', paddingTop: 8 }}>
              <button className="artemy-btn artemy-btn-ghost" onClick={() => setOpen(false)}>Cancel</button>
              <button className="artemy-btn artemy-btn-primary" onClick={handleCreate} disabled={saving || !form.title}>
                {saving ? 'Saving…' : 'Log Risk'}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <style>{`.artemy-tr-hover:hover { background: rgba(255,255,255,.03); }`}</style>
      <div style={{ height: 40 }} />
    </div>
  );
}
